import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { Typography, Segmented } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  PercentageOutlined,
  DollarOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  LockOutlined,
  ReloadOutlined,
  CrownOutlined,
  TeamOutlined,
  WarningOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  PieChartOutlined,
  AppstoreOutlined,
  FireOutlined,
  UserAddOutlined,
  WalletOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import { telegram } from '../../utils/telegram';
import { businessApi } from '../../api/businessApi';
import { useBusinessStore } from '../../store/businessStore';
import { useCountUp } from '../../hooks/useCountUp';
import OwnerAnalyticsSkeleton from './OwnerAnalyticsSkeleton';
import type { AnalyticsPeriod as PeriodKey } from '../../types/provider';
import type {
  OwnerAnalyticsData,
  OwnerProviderStat,
} from '../../types/business';

const { Text } = Typography;

const PERIOD_KEYS: PeriodKey[] = ['week', 'month', 'quarter', 'year'];

// ─── Status colors ────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  PENDING: '#faad14',
  CONFIRMED: '#1677ff',
  COMPLETED: '#1f8b4c',
  CANCELLED: '#ff4d4f',
};

// ─── Revenue share palette ────────────────────────────────────────────────────

const SHARE_PALETTE = [
  '#4f46e5',
  '#0ea5e9',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#94a3b8', // others
];

// ─── Locale map (CLAUDE.md 15.5) ─────────────────────────────────────────────

const LOCALE_MAP: Record<string, string> = {
  uz: 'uz-UZ',
  ru: 'ru-RU',
  en: 'en-US',
};

// ─── Error type guard — no unsafe `as` cast ───────────────────────────────────

function isApiError(
  err: unknown,
): err is { response?: { data?: { code?: string } } } {
  return typeof err === 'object' && err !== null && 'response' in err;
}

type AnalyticsError = 'premium' | 'generic';

function classifyError(err: unknown): AnalyticsError {
  if (isApiError(err)) {
    const code = err.response?.data?.code;
    if (code === 'FEATURE_REQUIRES_PREMIUM_PLAN') return 'premium';
  }
  return 'generic';
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCompactNumber(
  value: number,
  currencyLabel: string,
  locale: string,
): string {
  if (value >= 1_000_000) {
    const millions = value / 1_000_000;
    return `${millions
      .toFixed(millions < 10 ? 2 : 1)
      .replace(/\.?0+$/, '')} mln ${currencyLabel}`;
  }
  if (value >= 1_000) {
    const thousands = value / 1_000;
    return `${thousands.toFixed(0)}k ${currencyLabel}`;
  }
  return `${value.toLocaleString(locale)} ${currencyLabel}`;
}

function formatFullNumber(value: number, locale: string): string {
  return value.toLocaleString(locale);
}

function computeTrend(
  current: number,
  previous: number,
): { direction: 'up' | 'down' | 'flat'; percent: number } {
  if (previous === 0) {
    return { direction: current > 0 ? 'up' : 'flat', percent: 0 };
  }
  const diff = ((current - previous) / previous) * 100;
  if (Math.abs(diff) < 0.5) return { direction: 'flat', percent: 0 };
  return { direction: diff > 0 ? 'up' : 'down', percent: Math.abs(diff) };
}

// ─── AnimatedNumber — wraps useCountUp for use inside renders ────────────────

function AnimatedNumber({
  value,
  format,
  durationMs,
  enabled,
}: {
  value: number;
  format: (n: number) => string;
  durationMs?: number;
  enabled?: boolean;
}) {
  const animated = useCountUp(value, { durationMs, enabled });
  return <>{format(animated)}</>;
}

// ─── useGrowOnMount — bars/charts grow from 0 on mount ───────────────────────

function useGrowOnMount(): boolean {
  const reducedRef = useRef<boolean>(
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false,
  );
  const reduced = reducedRef.current;
  const [grown, setGrown] = useState(reduced);
  useEffect(() => {
    if (reduced) return;
    const id = requestAnimationFrame(() => setGrown(true));
    return () => cancelAnimationFrame(id);
  }, [reduced]);
  return grown;
}

// ─── PeriodSwitcher ──────────────────────────────────────────────────────────

function PeriodSwitcher({
  value,
  onChange,
}: {
  value: PeriodKey;
  onChange: (next: PeriodKey) => void;
}) {
  const { t } = useTranslation('owner');
  return (
    <Segmented
      block
      value={value}
      onChange={(v) => {
        telegram.hapticFeedback.selection();
        if (
          typeof v === 'string' &&
          (PERIOD_KEYS as readonly string[]).includes(v)
        ) {
          onChange(v as PeriodKey);
        }
      }}
      options={PERIOD_KEYS.map((key) => ({
        value: key,
        label: t(`analytics.period.${key}`),
      }))}
    />
  );
}

// ─── SectionHeader ───────────────────────────────────────────────────────────

function SectionHeader({
  title,
  subtitle,
  icon,
  accentColor,
}: {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  accentColor?: string;
}) {
  return (
    <div style={{ marginBottom: 10, marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {icon && accentColor && (
          <div
            style={{
              width: 26,
              height: 26,
              borderRadius: 8,
              background: accentColor + '1a',
              color: accentColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            {icon}
          </div>
        )}
        <Text
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: 'var(--bl-text-strong, #0f172a)',
            display: 'block',
          }}
        >
          {title}
        </Text>
      </div>
      {subtitle && (
        <Text
          style={{
            fontSize: 11,
            color: 'var(--bl-text-muted, #9ca3af)',
            display: 'block',
            marginTop: 2,
          }}
        >
          {subtitle}
        </Text>
      )}
    </div>
  );
}

// ─── HeroRevenueCard ─────────────────────────────────────────────────────────

function HeroRevenueCard({
  data,
  locale,
}: {
  data: OwnerAnalyticsData;
  locale: string;
}) {
  const { t } = useTranslation('owner');
  const trend = computeTrend(data.totalRevenue, data.prevRevenue);
  const currencyLabel = t('analytics.currency');

  const trendChipBg =
    trend.direction === 'up'
      ? 'rgba(255,255,255,0.20)'
      : trend.direction === 'down'
        ? 'rgba(255,77,79,0.22)'
        : 'rgba(255,255,255,0.16)';

  const trendIcon =
    trend.direction === 'up' ? (
      <ArrowUpOutlined className="bl-pulse-soft" />
    ) : trend.direction === 'down' ? (
      <ArrowDownOutlined />
    ) : null;

  const points = data.revenueByPoint;
  const max = Math.max(...points.map((p) => p.value), 1);
  const spark = points
    .map((p, i) => {
      const x = (i / Math.max(points.length - 1, 1)) * 100;
      const y = 100 - (p.value / max) * 100;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 18,
        background:
          'linear-gradient(135deg, #1f8b4c 0%, #27ae60 55%, #2dba71 100%)',
        padding: '16px 16px 18px',
        marginBottom: 12,
        overflow: 'hidden',
        boxShadow: '0 6px 20px rgba(31,139,76,0.25)',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -40,
          right: -30,
          width: 140,
          height: 140,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.10)',
          filter: 'blur(6px)',
        }}
      />

      <div
        style={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              fontSize: 11,
              color: 'rgba(255,255,255,0.75)',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              fontWeight: 600,
              display: 'block',
              marginBottom: 4,
            }}
          >
            {t('analytics.hero.revenueLabel')}
          </Text>
          <Text
            style={{
              fontSize: 26,
              fontWeight: 800,
              color: '#fff',
              letterSpacing: -0.6,
              display: 'block',
              lineHeight: 1.15,
            }}
          >
            <AnimatedNumber
              value={data.totalRevenue}
              format={(n) => formatFullNumber(Math.round(n), locale)}
              durationMs={1300}
            />
            <span
              style={{ fontSize: 14, fontWeight: 500, marginLeft: 6, opacity: 0.8 }}
            >
              {currencyLabel}
            </span>
          </Text>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 10,
            }}
          >
            {trend.direction !== 'flat' && (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#fff',
                  background: trendChipBg,
                  borderRadius: 20,
                  padding: '3px 10px',
                  backdropFilter: 'blur(6px)',
                }}
              >
                {trendIcon}
                <span>{trend.percent.toFixed(1)}%</span>
              </div>
            )}
            <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>
              {trend.direction === 'flat'
                ? t('analytics.hero.trendFlat')
                : t('analytics.hero.comparedTo')}
            </Text>
          </div>
        </div>

        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{
            width: 84,
            height: 40,
            marginLeft: 12,
            flexShrink: 0,
            opacity: 0.95,
          }}
        >
          <defs>
            <linearGradient id="ownerSparkFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fff" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
          </defs>
          <polyline
            points={spark}
            fill="none"
            stroke="#fff"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <polygon
            points={`0,100 ${spark} 100,100`}
            fill="url(#ownerSparkFill)"
          />
        </svg>
      </div>
    </div>
  );
}

// ─── KpiGrid ─────────────────────────────────────────────────────────────────

function KpiGrid({ data, locale }: { data: OwnerAnalyticsData; locale: string }) {
  const { t } = useTranslation('owner');
  const currencyLabel = t('analytics.currency');
  const [pressedIdx, setPressedIdx] = useState<number | null>(null);

  const cards = [
    {
      label: t('analytics.kpi.totalBookings'),
      rawValue: data.totalBookings,
      format: (n: number) => formatFullNumber(Math.round(n), locale),
      icon: <CalendarOutlined />,
      color: '#1677ff',
      bg: 'var(--bl-info-soft, #e7f1fe)',
    },
    {
      label: t('analytics.kpi.completed'),
      rawValue: data.completedBookings,
      format: (n: number) => formatFullNumber(Math.round(n), locale),
      icon: <CheckCircleOutlined />,
      color: '#1f8b4c',
      bg: 'var(--bl-success-soft, #e8f7ee)',
    },
    {
      label: t('analytics.kpi.completionRate'),
      rawValue: data.completionRate,
      format: (n: number) => `${n.toFixed(1)}%`,
      icon: <PercentageOutlined />,
      color: '#d46b08',
      bg: '#fff8ed',
    },
    {
      label: t('analytics.kpi.avgRevenue'),
      rawValue: data.avgRevenue,
      format: (n: number) =>
        formatCompactNumber(Math.round(n), currencyLabel, locale),
      icon: <DollarOutlined />,
      color: 'var(--bl-text-strong, #0f172a)',
      bg: 'var(--bl-surface-soft, #f7f8fa)',
    },
  ];

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 8,
        marginBottom: 12,
      }}
    >
      {cards.map((c, idx) => (
        <div
          key={c.label}
          onTouchStart={() => {
            setPressedIdx(idx);
            telegram.hapticFeedback.selection();
          }}
          onTouchEnd={() => setPressedIdx(null)}
          onTouchCancel={() => setPressedIdx(null)}
          onMouseDown={() => setPressedIdx(idx)}
          onMouseUp={() => setPressedIdx(null)}
          onMouseLeave={() => setPressedIdx(null)}
          style={{
            borderRadius: 14,
            background: c.bg,
            padding: '12px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            minHeight: 92,
            transform: pressedIdx === idx ? 'scale(0.98)' : 'scale(1)',
            transition: 'transform 0.1s ease',
            WebkitTapHighlightColor: 'transparent',
            cursor: 'default',
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              background: c.color + '20',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 15,
              color: c.color,
            }}
          >
            {c.icon}
          </div>
          <div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 800,
                lineHeight: 1.15,
                color: 'var(--bl-text-strong, #0f172a)',
                letterSpacing: -0.3,
              }}
            >
              <AnimatedNumber
                value={c.rawValue}
                format={c.format}
                durationMs={1000}
              />
            </div>
            <Text
              style={{
                fontSize: 11,
                color: 'var(--bl-text-muted, #9ca3af)',
                fontWeight: 500,
                display: 'block',
                marginTop: 2,
              }}
            >
              {c.label}
            </Text>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── RevenueTrendChart ───────────────────────────────────────────────────────

function RevenueTrendChart({
  data,
}: {
  data: OwnerAnalyticsData;
}) {
  const { t } = useTranslation('owner');
  const currencyLabel = t('analytics.currency');
  const points = data.revenueByPoint;
  const max = Math.max(...points.map((p) => p.value), 1);
  const grown = useGrowOnMount();
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);

  const CHART_HEIGHT = 132;
  const BAR_GAP = 8;
  const labelHeight = 22;

  return (
    <div
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
        padding: '14px 14px 12px',
        marginBottom: 12,
      }}
    >
      <SectionHeader
        title={t('analytics.trend.title')}
        subtitle={t('analytics.trend.subtitle')}
        icon={<LineChartOutlined />}
        accentColor="#6366f1"
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: BAR_GAP,
          height: CHART_HEIGHT,
          marginTop: 8,
        }}
      >
        {points.map((p, i) => {
          const isMax = p.value === max;
          const isSelected = selectedIdx === i;
          const heightPct = grown
            ? max > 0
              ? (p.value / max) * 100
              : 0
            : 0;
          return (
            <div
              key={i}
              role="button"
              tabIndex={0}
              aria-label={`${p.label}: ${p.value}`}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                position: 'relative',
                cursor: 'pointer',
              }}
              onClick={() => {
                setSelectedIdx(isSelected ? null : i);
                telegram.hapticFeedback.selection();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedIdx(isSelected ? null : i);
                  telegram.hapticFeedback.selection();
                }
              }}
            >
              {isSelected && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--bl-text-strong, #0f172a)',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '3px 7px',
                    borderRadius: 6,
                    whiteSpace: 'nowrap',
                    marginBottom: 4,
                    zIndex: 1,
                  }}
                >
                  {p.value >= 1_000_000
                    ? `${(p.value / 1_000_000).toFixed(1)}M`
                    : p.value >= 1_000
                      ? `${(p.value / 1_000).toFixed(0)}k`
                      : p.value.toString()}
                </div>
              )}
              {!isSelected && (
                <Text
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: isMax ? '#1f8b4c' : 'var(--bl-text-muted, #9ca3af)',
                    marginBottom: 4,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {p.value >= 1_000_000
                    ? `${(p.value / 1_000_000).toFixed(1)}M`
                    : p.value >= 1_000
                      ? `${(p.value / 1_000).toFixed(0)}k`
                      : p.value.toString()}
                </Text>
              )}

              <div
                style={{
                  width: '100%',
                  maxWidth: 28,
                  height: `${heightPct}%`,
                  minHeight: grown && p.value > 0 ? 4 : 0,
                  borderRadius: '6px 6px 2px 2px',
                  background: isSelected
                    ? 'linear-gradient(180deg, #a78bfa 0%, #6366f1 100%)'
                    : isMax
                      ? 'linear-gradient(180deg, #2dba71 0%, #1f8b4c 100%)'
                      : 'linear-gradient(180deg, #aed8ff 0%, #66aaf0 100%)',
                  transition: 'height 0.35s ease',
                  opacity: selectedIdx !== null && !isSelected ? 0.5 : 1,
                }}
                title={`${p.label}: ${p.value}`}
              />
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          gap: BAR_GAP,
          marginTop: 6,
          height: labelHeight - 6,
        }}
      >
        {points.map((p, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              textAlign: 'center',
              fontSize: 11,
              color:
                selectedIdx === i
                  ? 'var(--bl-text-strong, #0f172a)'
                  : 'var(--bl-text-muted, #6b7280)',
              fontWeight: selectedIdx === i ? 700 : 500,
            }}
          >
            {p.label}
          </div>
        ))}
      </div>

      <Text
        style={{
          display: 'block',
          fontSize: 10,
          color: 'var(--bl-text-muted, #9ca3af)',
          marginTop: 4,
          textAlign: 'right',
        }}
      >
        {currencyLabel}
      </Text>
    </div>
  );
}

// ─── StatusBreakdown ─────────────────────────────────────────────────────────

function StatusBreakdown({ data }: { data: OwnerAnalyticsData }) {
  const { t } = useTranslation('owner');
  const grown = useGrowOnMount();

  const segments = [
    { key: 'COMPLETED', value: data.completedBookings, color: STATUS_COLORS.COMPLETED },
    { key: 'CONFIRMED', value: data.confirmedBookings, color: STATUS_COLORS.CONFIRMED },
    { key: 'PENDING', value: data.pendingBookings, color: STATUS_COLORS.PENDING },
    { key: 'CANCELLED', value: data.cancelledBookings, color: STATUS_COLORS.CANCELLED },
  ];

  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const visible = segments.filter((s) => s.value > 0);

  return (
    <div
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
        padding: '14px 14px 14px',
        marginBottom: 12,
      }}
    >
      <SectionHeader
        title={t('analytics.status.title')}
        icon={<CheckCircleOutlined />}
        accentColor="#3b82f6"
      />

      <div
        style={{
          display: 'flex',
          width: '100%',
          height: 10,
          borderRadius: 6,
          overflow: 'hidden',
          background: 'var(--bl-surface-soft, #f0f2f5)',
          marginTop: 4,
          marginBottom: 12,
        }}
      >
        {visible.map((seg) => {
          const widthPct = grown
            ? total > 0
              ? (seg.value / total) * 100
              : 0
            : 0;
          return (
            <div
              key={seg.key}
              style={{
                width: `${widthPct}%`,
                background: seg.color,
                transition: 'width 0.3s ease',
              }}
              title={`${seg.key}: ${seg.value}`}
            />
          );
        })}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {segments.map((seg) => {
          const pct = total > 0 ? (seg.value / total) * 100 : 0;
          return (
            <div
              key={seg.key}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <div
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: seg.color,
                  flexShrink: 0,
                }}
              />
              <div
                style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}
              >
                <Text
                  style={{
                    fontSize: 11,
                    color: 'var(--bl-text-muted, #6b7280)',
                    lineHeight: 1.2,
                  }}
                >
                  {t(`analytics.status.labels.${seg.key}`)}
                </Text>
                <Text
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--bl-text-strong, #0f172a)',
                    lineHeight: 1.2,
                  }}
                >
                  {seg.value}
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 500,
                      color: 'var(--bl-text-muted, #9ca3af)',
                      marginLeft: 4,
                    }}
                  >
                    {pct.toFixed(0)}%
                  </span>
                </Text>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── TopProvidersList ─────────────────────────────────────────────────────────

function TopProvidersList({
  data,
  locale,
}: {
  data: OwnerAnalyticsData;
  locale: string;
}) {
  const { t } = useTranslation('owner');
  const currencyLabel = t('analytics.currency');
  const grown = useGrowOnMount();
  const [pressedIdx, setPressedIdx] = useState<number | null>(null);

  const sorted = [...data.providers]
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  if (sorted.length === 0) {
    return (
      <div
        style={{
          borderRadius: 16,
          background: 'var(--bl-surface, #fff)',
          boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
          padding: '14px 14px 16px',
          marginBottom: 12,
        }}
      >
        <SectionHeader
          title={t('analytics.topProviders.title')}
          subtitle={t('analytics.topProviders.subtitle')}
          icon={<TrophyOutlined />}
          accentColor="#f59e0b"
        />
        <Text style={{ fontSize: 12, color: 'var(--bl-text-muted, #9ca3af)' }}>
          {t('analytics.topProviders.empty')}
        </Text>
      </div>
    );
  }

  const maxRevenue = Math.max(...sorted.map((p) => p.revenue), 1);

  return (
    <div
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
        padding: '14px 14px 14px',
        marginBottom: 12,
      }}
    >
      <SectionHeader
        title={t('analytics.topProviders.title')}
        subtitle={t('analytics.topProviders.subtitle')}
        icon={<TrophyOutlined />}
        accentColor="#f59e0b"
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {sorted.map((p, idx) => {
          const widthPct = grown ? (p.revenue / maxRevenue) * 100 : 0;
          const isPressed = pressedIdx === idx;
          return (
            <div
              key={p.id}
              onTouchStart={() => {
                setPressedIdx(idx);
                telegram.hapticFeedback.selection();
              }}
              onTouchEnd={() => setPressedIdx(null)}
              onTouchCancel={() => setPressedIdx(null)}
              onMouseDown={() => setPressedIdx(idx)}
              onMouseUp={() => setPressedIdx(null)}
              onMouseLeave={() => setPressedIdx(null)}
              style={{
                transform: isPressed ? 'scale(0.98)' : 'scale(1)',
                transition: 'transform 0.1s ease',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 5,
                  gap: 8,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 6,
                      background:
                        idx === 0
                          ? 'linear-gradient(135deg, #4f46e5, #6366f1)'
                          : 'var(--bl-surface-soft, #f0f2f5)',
                      color:
                        idx === 0 ? '#fff' : 'var(--bl-text-muted, #6b7280)',
                      fontSize: 11,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {idx + 1}
                  </div>
                  <Text
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: 'var(--bl-text-strong, #0f172a)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {p.name}
                  </Text>
                </div>
                <Text
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--bl-text-strong, #0f172a)',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <AnimatedNumber
                    value={p.revenue}
                    format={(n) =>
                      formatCompactNumber(Math.round(n), currencyLabel, locale)
                    }
                    durationMs={900}
                    enabled={false}
                  />
                </Text>
              </div>

              <div
                style={{
                  height: 6,
                  borderRadius: 4,
                  background: 'var(--bl-surface-soft, #f0f2f5)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${widthPct}%`,
                    height: '100%',
                    background:
                      idx === 0
                        ? 'linear-gradient(90deg, #4f46e5, #6366f1)'
                        : 'linear-gradient(90deg, #a5b4fc, #818cf8)',
                    transition: 'width 0.4s ease',
                  }}
                />
              </div>

              <Text
                style={{
                  fontSize: 10,
                  color: 'var(--bl-text-muted, #9ca3af)',
                  marginTop: 3,
                  display: 'block',
                }}
              >
                {t('analytics.topProviders.bookings', { count: p.bookings })}
              </Text>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── RevenueShareBar ─────────────────────────────────────────────────────────

function buildShareSegments(
  providers: OwnerProviderStat[],
  othersLabel: string,
): { label: string; percent: number; color: string }[] {
  const top5 = [...providers]
    .sort((a, b) => b.sharePercent - a.sharePercent)
    .slice(0, 5);
  const top5Sum = top5.reduce((s, p) => s + p.sharePercent, 0);
  const othersPercent = Math.max(0, 100 - top5Sum);

  const segments = top5.map((p, idx) => ({
    label: p.name,
    percent: p.sharePercent,
    color: SHARE_PALETTE[idx] ?? SHARE_PALETTE[0],
  }));

  if (othersPercent > 0.5) {
    segments.push({
      label: othersLabel,
      percent: othersPercent,
      color: SHARE_PALETTE[5],
    });
  }

  return segments;
}

function RevenueShareBar({ data }: { data: OwnerAnalyticsData }) {
  const { t } = useTranslation('owner');
  const othersLabel = t('analytics.revenueShare.others');
  const grown = useGrowOnMount();
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  if (data.providers.length === 0) {
    return (
      <div
        style={{
          borderRadius: 16,
          background: 'var(--bl-surface, #fff)',
          boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
          padding: '14px 14px 16px',
          marginBottom: 12,
        }}
      >
        <SectionHeader
          title={t('analytics.revenueShare.title')}
          subtitle={t('analytics.revenueShare.subtitle')}
          icon={<PieChartOutlined />}
          accentColor="#6366f1"
        />
        <Text style={{ fontSize: 12, color: 'var(--bl-text-muted, #9ca3af)' }}>
          {t('analytics.revenueShare.empty')}
        </Text>
      </div>
    );
  }

  const segments = buildShareSegments(data.providers, othersLabel);

  return (
    <div
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
        padding: '14px 14px 14px',
        marginBottom: 12,
      }}
    >
      <SectionHeader
        title={t('analytics.revenueShare.title')}
        subtitle={t('analytics.revenueShare.subtitle')}
        icon={<PieChartOutlined />}
        accentColor="#6366f1"
      />

      {/* Horizontal stacked bar */}
      <div
        style={{
          display: 'flex',
          width: '100%',
          height: 14,
          borderRadius: 8,
          overflow: 'hidden',
          background: 'var(--bl-surface-soft, #f0f2f5)',
          marginBottom: 14,
        }}
      >
        {segments.map((seg) => {
          const isSelected = selectedLabel === seg.label;
          return (
            <div
              key={seg.label}
              role="button"
              tabIndex={0}
              aria-label={`${seg.label}: ${seg.percent.toFixed(1)}%`}
              onClick={() => {
                setSelectedLabel(isSelected ? null : seg.label);
                telegram.hapticFeedback.selection();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedLabel(isSelected ? null : seg.label);
                  telegram.hapticFeedback.selection();
                }
              }}
              style={{
                width: grown ? `${seg.percent}%` : '0%',
                height: '100%',
                background: seg.color,
                transition: 'width 0.4s ease, opacity 0.15s ease',
                minWidth: grown && seg.percent > 0.5 ? 2 : 0,
                opacity:
                  selectedLabel !== null && !isSelected ? 0.4 : 1,
                cursor: 'pointer',
              }}
              title={`${seg.label}: ${seg.percent.toFixed(1)}%`}
            />
          );
        })}
      </div>

      {/* Selected tooltip */}
      {selectedLabel !== null && (
        <div
          style={{
            background: 'var(--bl-surface-soft, #f7f8fa)',
            borderRadius: 8,
            padding: '6px 10px',
            marginBottom: 10,
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--bl-text-strong, #0f172a)',
          }}
        >
          {selectedLabel}:{' '}
          {(
            segments.find((s) => s.label === selectedLabel)?.percent ?? 0
          ).toFixed(1)}
          %
        </div>
      )}

      {/* Legend */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {segments.map((seg) => (
          <div
            key={seg.label}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              opacity:
                selectedLabel !== null && selectedLabel !== seg.label
                  ? 0.4
                  : 1,
              transition: 'opacity 0.15s ease',
            }}
          >
            <div
              style={{
                width: 9,
                height: 9,
                borderRadius: '50%',
                background: seg.color,
                flexShrink: 0,
              }}
            />
            <Text
              style={{
                fontSize: 12,
                color: 'var(--bl-text-strong, #0f172a)',
                flex: 1,
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {seg.label}
            </Text>
            <Text
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: 'var(--bl-text-muted, #6b7280)',
                flexShrink: 0,
              }}
            >
              {seg.percent.toFixed(1)}%
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── UtilizationList ─────────────────────────────────────────────────────────

function UtilizationList({ data }: { data: OwnerAnalyticsData }) {
  const { t } = useTranslation('owner');
  const grown = useGrowOnMount();

  if (data.providers.length === 0) {
    return (
      <div
        style={{
          borderRadius: 16,
          background: 'var(--bl-surface, #fff)',
          boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
          padding: '14px 14px 16px',
          marginBottom: 12,
        }}
      >
        <SectionHeader
          title={t('analytics.utilization.title')}
          subtitle={t('analytics.utilization.subtitle')}
          icon={<ClockCircleOutlined />}
          accentColor="#0ea5e9"
        />
        <Text style={{ fontSize: 12, color: 'var(--bl-text-muted, #9ca3af)' }}>
          {t('analytics.utilization.empty')}
        </Text>
      </div>
    );
  }

  // Sort by utilizationPercent desc (null goes last)
  const sorted = [...data.providers].sort((a, b) => {
    if (a.utilizationPercent === null && b.utilizationPercent === null) return 0;
    if (a.utilizationPercent === null) return 1;
    if (b.utilizationPercent === null) return -1;
    return b.utilizationPercent - a.utilizationPercent;
  });

  return (
    <div
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
        padding: '14px 14px 14px',
        marginBottom: 12,
      }}
    >
      <SectionHeader
        title={t('analytics.utilization.title')}
        subtitle={t('analytics.utilization.subtitle')}
        icon={<ClockCircleOutlined />}
        accentColor="#0ea5e9"
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {sorted.map((p) => (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Text
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--bl-text-strong, #0f172a)',
                width: 90,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {p.name}
            </Text>

            {p.utilizationPercent === null ? (
              <Text
                style={{
                  fontSize: 11,
                  color: 'var(--bl-text-muted, #9ca3af)',
                  flex: 1,
                  fontStyle: 'italic',
                }}
              >
                {t('analytics.utilization.noSchedule')}
              </Text>
            ) : (
              <>
                <div
                  style={{
                    flex: 1,
                    height: 10,
                    borderRadius: 6,
                    background: 'var(--bl-surface-soft, #f0f2f5)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: grown
                        ? `${Math.min(p.utilizationPercent, 100)}%`
                        : '0%',
                      height: '100%',
                      background:
                        p.utilizationPercent >= 80
                          ? 'linear-gradient(90deg, #1f8b4c, #2dba71)'
                          : p.utilizationPercent >= 50
                            ? 'linear-gradient(90deg, #0ea5e9, #38bdf8)'
                            : 'linear-gradient(90deg, #94a3b8, #cbd5e1)',
                      transition: 'width 0.4s ease',
                    }}
                  />
                </div>
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'var(--bl-text-strong, #0f172a)',
                    width: 36,
                    textAlign: 'right',
                    flexShrink: 0,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {p.utilizationPercent.toFixed(0)}%
                </Text>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── WeekdayLoadChart ─────────────────────────────────────────────────────────

function WeekdayLoadChart({ data }: { data: OwnerAnalyticsData }) {
  const { t } = useTranslation('owner');
  const items = data.weekdayLoad;
  const grown = useGrowOnMount();
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);

  const CHART_HEIGHT = 110;
  const BAR_GAP = 6;

  if (items.length === 0) {
    return (
      <div
        style={{
          borderRadius: 16,
          background: 'var(--bl-surface, #fff)',
          boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
          padding: '14px 14px 16px',
          marginBottom: 12,
        }}
      >
        <SectionHeader
          title={t('analytics.weekdayLoad.title')}
          subtitle={t('analytics.weekdayLoad.subtitle')}
          icon={<CalendarOutlined />}
          accentColor="#10b981"
        />
        <Text style={{ fontSize: 12, color: 'var(--bl-text-muted, #9ca3af)' }}>
          {t('analytics.weekdayLoad.empty')}
        </Text>
      </div>
    );
  }

  const maxBookings = Math.max(...items.map((d) => d.bookings), 1);

  return (
    <div
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
        padding: '14px 14px 14px',
        marginBottom: 12,
      }}
    >
      <SectionHeader
        title={t('analytics.weekdayLoad.title')}
        subtitle={t('analytics.weekdayLoad.subtitle')}
        icon={<CalendarOutlined />}
        accentColor="#10b981"
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: BAR_GAP,
          height: CHART_HEIGHT,
          marginTop: 8,
        }}
      >
        {items.map((d, idx) => {
          const isMax = d.bookings === maxBookings && maxBookings > 0;
          const isSelected = selectedIdx === idx;
          const heightPct = grown
            ? maxBookings > 0
              ? (d.bookings / maxBookings) * 100
              : 0
            : 0;
          return (
            <div
              key={d.weekday}
              role="button"
              tabIndex={0}
              aria-label={`${t(`analytics.weekdayLoad.days.${d.weekday}`)}: ${d.bookings}`}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                cursor: 'pointer',
                position: 'relative',
              }}
              onClick={() => {
                setSelectedIdx(isSelected ? null : idx);
                telegram.hapticFeedback.selection();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedIdx(isSelected ? null : idx);
                  telegram.hapticFeedback.selection();
                }
              }}
            >
              {isSelected && d.bookings > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--bl-text-strong, #0f172a)',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '3px 7px',
                    borderRadius: 6,
                    whiteSpace: 'nowrap',
                    marginBottom: 4,
                    zIndex: 1,
                  }}
                >
                  {t(`analytics.weekdayLoad.days.${d.weekday}`)}{' '}
                  {t('analytics.weekdayLoad.bookings', { count: d.bookings })}
                </div>
              )}
              {!isSelected && d.bookings > 0 && (
                <Text
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: isMax ? '#4f46e5' : 'var(--bl-text-muted, #9ca3af)',
                    marginBottom: 3,
                  }}
                >
                  {d.bookings}
                </Text>
              )}
              <div
                style={{
                  width: '100%',
                  maxWidth: 28,
                  height: `${heightPct}%`,
                  minHeight: grown && d.bookings > 0 ? 4 : 0,
                  borderRadius: '6px 6px 2px 2px',
                  background: isSelected
                    ? 'linear-gradient(180deg, #34d399 0%, #10b981 100%)'
                    : isMax
                      ? 'linear-gradient(180deg, #6366f1 0%, #4f46e5 100%)'
                      : 'linear-gradient(180deg, #c7d2fe 0%, #a5b4fc 100%)',
                  transition: 'height 0.35s ease',
                  opacity:
                    selectedIdx !== null && !isSelected ? 0.45 : 1,
                }}
                title={`${d.weekday}: ${d.bookings}`}
              />
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          gap: BAR_GAP,
          marginTop: 6,
        }}
      >
        {items.map((d, idx) => (
          <div
            key={d.weekday}
            style={{
              flex: 1,
              textAlign: 'center',
              fontSize: 11,
              color:
                selectedIdx === idx
                  ? 'var(--bl-text-strong, #0f172a)'
                  : 'var(--bl-text-muted, #6b7280)',
              fontWeight: selectedIdx === idx ? 700 : 500,
            }}
          >
            {t(`analytics.weekdayLoad.days.${d.weekday}`)}
          </div>
        ))}
      </div>

      <Text
        style={{
          display: 'block',
          fontSize: 10,
          color: 'var(--bl-text-muted, #9ca3af)',
          marginTop: 4,
          textAlign: 'right',
        }}
      >
        {t('analytics.weekdayLoad.bookings', { count: maxBookings })}
      </Text>
    </div>
  );
}

// ─── TopServicesList ─────────────────────────────────────────────────────────

function TopServicesList({
  data,
  locale,
}: {
  data: OwnerAnalyticsData;
  locale: string;
}) {
  const { t } = useTranslation('owner');
  const currencyLabel = t('analytics.currency');
  const grown = useGrowOnMount();
  const items = data.topServices;

  if (items.length === 0) {
    return (
      <div
        style={{
          borderRadius: 16,
          background: 'var(--bl-surface, #fff)',
          boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
          padding: '14px 14px 16px',
          marginBottom: 12,
        }}
      >
        <SectionHeader
          title={t('analytics.topServices.title')}
          subtitle={t('analytics.topServices.subtitle')}
          icon={<AppstoreOutlined />}
          accentColor="#8b5cf6"
        />
        <Text style={{ fontSize: 12, color: 'var(--bl-text-muted, #9ca3af)' }}>
          {t('analytics.topServices.empty')}
        </Text>
      </div>
    );
  }

  const max = Math.max(...items.map((s) => s.revenue), 1);

  return (
    <div
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
        padding: '14px 14px 14px',
        marginBottom: 12,
      }}
    >
      <SectionHeader
        title={t('analytics.topServices.title')}
        subtitle={t('analytics.topServices.subtitle')}
        icon={<AppstoreOutlined />}
        accentColor="#8b5cf6"
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.map((s, idx) => {
          const widthPct = grown ? (s.revenue / max) * 100 : 0;
          return (
            <div key={s.id}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 5,
                  gap: 8,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 6,
                      background:
                        idx === 0
                          ? 'linear-gradient(135deg, #fa8c16, #faad14)'
                          : 'var(--bl-surface-soft, #f0f2f5)',
                      color:
                        idx === 0 ? '#fff' : 'var(--bl-text-muted, #6b7280)',
                      fontSize: 11,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {idx + 1}
                  </div>
                  <Text
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: 'var(--bl-text-strong, #0f172a)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {s.name}
                  </Text>
                </div>
                <Text
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--bl-text-strong, #0f172a)',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <AnimatedNumber
                    value={s.revenue}
                    format={(n) =>
                      formatCompactNumber(Math.round(n), currencyLabel, locale)
                    }
                    durationMs={900}
                    enabled={false}
                  />
                </Text>
              </div>

              <div
                style={{
                  height: 6,
                  borderRadius: 4,
                  background: 'var(--bl-surface-soft, #f0f2f5)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${widthPct}%`,
                    height: '100%',
                    background:
                      idx === 0
                        ? 'linear-gradient(90deg, #fa8c16, #faad14)'
                        : 'linear-gradient(90deg, #66aaf0, #3390ec)',
                    transition: 'width 0.4s ease',
                  }}
                />
              </div>

              <Text
                style={{
                  fontSize: 10,
                  color: 'var(--bl-text-muted, #9ca3af)',
                  marginTop: 3,
                  display: 'block',
                }}
              >
                {t('analytics.topServices.bookingsCount', { count: s.bookings })}
              </Text>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── TopCustomersList ────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function TopCustomersList({
  data,
  locale,
}: {
  data: OwnerAnalyticsData;
  locale: string;
}) {
  const { t } = useTranslation('owner');
  const currencyLabel = t('analytics.currency');
  const items = data.topCustomers;

  if (items.length === 0) {
    return (
      <div
        style={{
          borderRadius: 16,
          background: 'var(--bl-surface, #fff)',
          boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
          padding: '14px 14px 16px',
          marginBottom: 12,
        }}
      >
        <SectionHeader
          title={t('analytics.topCustomers.title')}
          subtitle={t('analytics.topCustomers.subtitle')}
          icon={<TeamOutlined />}
          accentColor="#ec4899"
        />
        <Text style={{ fontSize: 12, color: 'var(--bl-text-muted, #9ca3af)' }}>
          {t('analytics.topCustomers.empty')}
        </Text>
      </div>
    );
  }

  const avatarPalette = [
    '#3390ec',
    '#1f8b4c',
    '#fa8c16',
    '#9254de',
    '#eb2f96',
  ];

  return (
    <div
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
        padding: '14px 14px 8px',
        marginBottom: 12,
      }}
    >
      <SectionHeader
        title={t('analytics.topCustomers.title')}
        subtitle={t('analytics.topCustomers.subtitle')}
        icon={<TeamOutlined />}
        accentColor="#ec4899"
      />

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {items.map((c, idx) => {
          const color = avatarPalette[idx % avatarPalette.length];
          const isLast = idx === items.length - 1;
          return (
            <div
              key={c.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 0',
                borderBottom: isLast
                  ? 'none'
                  : '1px solid var(--bl-divider, #f0f0f0)',
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  background: color + '18',
                  color,
                  fontSize: 12,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {getInitials(c.name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--bl-text-strong, #0f172a)',
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {c.name}
                </Text>
                <Text
                  style={{
                    fontSize: 11,
                    color: 'var(--bl-text-muted, #9ca3af)',
                    display: 'block',
                  }}
                >
                  {t('analytics.topCustomers.bookings', { count: c.bookings })}
                </Text>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <Text
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--bl-text-strong, #0f172a)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <AnimatedNumber
                    value={c.totalSpent}
                    format={(n) =>
                      formatCompactNumber(Math.round(n), currencyLabel, locale)
                    }
                    durationMs={900}
                    enabled={false}
                  />
                </Text>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── PeakHoursChart ──────────────────────────────────────────────────────────

function PeakHoursChart({ data }: { data: OwnerAnalyticsData }) {
  const { t } = useTranslation('owner');
  const items = data.peakHours;
  const grown = useGrowOnMount();

  if (items.length === 0) {
    return (
      <div
        style={{
          borderRadius: 16,
          background: 'var(--bl-surface, #fff)',
          boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
          padding: '14px 14px 16px',
          marginBottom: 12,
        }}
      >
        <SectionHeader
          title={t('analytics.peakHours.title')}
          subtitle={t('analytics.peakHours.subtitle')}
          icon={<FireOutlined />}
          accentColor="#ef4444"
        />
        <Text style={{ fontSize: 12, color: 'var(--bl-text-muted, #9ca3af)' }}>
          {t('analytics.peakHours.empty')}
        </Text>
      </div>
    );
  }

  const max = Math.max(...items.map((h) => h.bookings), 1);

  return (
    <div
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
        padding: '14px 14px 14px',
        marginBottom: 12,
      }}
    >
      <SectionHeader
        title={t('analytics.peakHours.title')}
        subtitle={t('analytics.peakHours.subtitle')}
        icon={<FireOutlined />}
        accentColor="#ef4444"
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((h, idx) => {
          const widthPct = grown ? (h.bookings / max) * 100 : 0;
          const intensity =
            idx === 0 ? '#9254de' : idx === 1 ? '#b37feb' : '#d3adf7';
          return (
            <div
              key={h.hour}
              style={{ display: 'flex', alignItems: 'center', gap: 10 }}
            >
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--bl-text-strong, #0f172a)',
                  width: 44,
                  flexShrink: 0,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {h.hour}
              </Text>
              <div
                style={{
                  flex: 1,
                  height: 22,
                  borderRadius: 6,
                  background: 'var(--bl-surface-soft, #f7f8fa)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    width: `${widthPct}%`,
                    height: '100%',
                    background: `linear-gradient(90deg, ${intensity}, ${intensity}cc)`,
                    borderRadius: 6,
                    transition: 'width 0.4s ease',
                  }}
                />
              </div>
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'var(--bl-text-strong, #0f172a)',
                  width: 36,
                  textAlign: 'right',
                  flexShrink: 0,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {h.bookings}
              </Text>
            </div>
          );
        })}
      </div>

      <Text
        style={{
          display: 'block',
          fontSize: 10,
          color: 'var(--bl-text-muted, #9ca3af)',
          marginTop: 8,
          textAlign: 'right',
        }}
      >
        {t('analytics.peakHours.bookings', { count: max })}
      </Text>
    </div>
  );
}

// ─── CustomersBreakdown ──────────────────────────────────────────────────────

function CustomersBreakdown({ data }: { data: OwnerAnalyticsData }) {
  const { t } = useTranslation('owner');
  const total = data.newCustomers + data.returningCustomers;
  const grown = useGrowOnMount();
  const [pressedIdx, setPressedIdx] = useState<number | null>(null);

  const newPct = total > 0 ? (data.newCustomers / total) * 100 : 50;
  const grownNewPct = grown ? newPct : 0;

  const cards = [
    {
      label: t('analytics.customersBreakdown.new'),
      desc: t('analytics.customersBreakdown.newDesc'),
      rawValue: data.newCustomers,
      color: '#3390ec',
      bg: 'var(--bl-info-soft, #e7f1fe)',
    },
    {
      label: t('analytics.customersBreakdown.returning'),
      desc: t('analytics.customersBreakdown.returningDesc'),
      rawValue: data.returningCustomers,
      color: '#1f8b4c',
      bg: 'var(--bl-success-soft, #e8f7ee)',
    },
  ];

  return (
    <div
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
        padding: '14px 14px 14px',
        marginBottom: 12,
      }}
    >
      <SectionHeader
        title={t('analytics.customersBreakdown.title')}
        icon={<UserAddOutlined />}
        accentColor="#14b8a6"
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        {cards.map((c, idx) => (
          <div
            key={c.label}
            onTouchStart={() => {
              setPressedIdx(idx);
              telegram.hapticFeedback.selection();
            }}
            onTouchEnd={() => setPressedIdx(null)}
            onTouchCancel={() => setPressedIdx(null)}
            onMouseDown={() => setPressedIdx(idx)}
            onMouseUp={() => setPressedIdx(null)}
            onMouseLeave={() => setPressedIdx(null)}
            style={{
              borderRadius: 12,
              background: c.bg,
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              transform: pressedIdx === idx ? 'scale(0.98)' : 'scale(1)',
              transition: 'transform 0.1s ease',
              WebkitTapHighlightColor: 'transparent',
              cursor: 'default',
            }}
          >
            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: 'var(--bl-text-strong, #0f172a)',
                letterSpacing: -0.3,
                lineHeight: 1.1,
              }}
            >
              <AnimatedNumber
                value={c.rawValue}
                format={(n) => String(Math.round(n))}
                durationMs={1000}
              />
            </div>
            <Text
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: c.color,
                display: 'block',
              }}
            >
              {c.label}
            </Text>
            <Text
              style={{
                fontSize: 10,
                color: 'var(--bl-text-muted, #9ca3af)',
                display: 'block',
              }}
            >
              {c.desc}
            </Text>
          </div>
        ))}
      </div>

      {/* Split proportion bar */}
      {total > 0 && (
        <div
          style={{
            display: 'flex',
            width: '100%',
            height: 8,
            borderRadius: 5,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${grownNewPct}%`,
              background: '#3390ec',
              transition: 'width 0.4s ease',
            }}
          />
          <div
            style={{
              flex: 1,
              background: '#1f8b4c',
            }}
          />
        </div>
      )}
    </div>
  );
}

// ─── NoShowCard ──────────────────────────────────────────────────────────────

function NoShowCard({ data }: { data: OwnerAnalyticsData }) {
  const { t } = useTranslation('owner');
  const [pressed, setPressed] = useState(false);

  const isHigh = data.noShowRate >= 20;
  const isMedium = data.noShowRate >= 10 && data.noShowRate < 20;

  const rateColor = isHigh ? '#ff4d4f' : isMedium ? '#faad14' : '#1f8b4c';
  const rateBg = isHigh
    ? 'rgba(255,77,79,0.08)'
    : isMedium
      ? 'rgba(250,173,20,0.10)'
      : 'var(--bl-success-soft, #e8f7ee)';

  return (
    <div
      onTouchStart={() => {
        setPressed(true);
        telegram.hapticFeedback.selection();
      }}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
        padding: '14px 14px 14px',
        marginBottom: 12,
        transform: pressed ? 'scale(0.98)' : 'scale(1)',
        transition: 'transform 0.1s ease',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <SectionHeader
        title={t('analytics.noShow.title')}
        icon={<WarningOutlined />}
        accentColor="#f97316"
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {/* Big rate indicator */}
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: 18,
            background: rateBg,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Text
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: rateColor,
              letterSpacing: -0.5,
              lineHeight: 1,
              display: 'block',
            }}
          >
            <AnimatedNumber
              value={data.noShowRate}
              format={(n) => `${n.toFixed(1)}%`}
              durationMs={1000}
            />
          </Text>
          <Text
            style={{
              fontSize: 9,
              color: rateColor,
              fontWeight: 600,
              opacity: 0.8,
              display: 'block',
              textAlign: 'center',
              lineHeight: 1.2,
              marginTop: 2,
            }}
          >
            {t('analytics.noShow.rate')}
          </Text>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: 'var(--bl-text-strong, #0f172a)',
              display: 'block',
              letterSpacing: -0.5,
              lineHeight: 1.1,
            }}
          >
            <AnimatedNumber
              value={data.noShowBookings}
              format={(n) => String(Math.round(n))}
              durationMs={1000}
            />
          </Text>
          <Text
            style={{
              fontSize: 12,
              color: 'var(--bl-text-muted, #6b7280)',
              display: 'block',
              marginTop: 2,
            }}
          >
            {t('analytics.noShow.count', { count: data.noShowBookings })}
          </Text>
        </div>
      </div>
    </div>
  );
}

// ─── PaymentCard ─────────────────────────────────────────────────────────────

function PaymentCard({
  data,
  locale,
}: {
  data: OwnerAnalyticsData;
  locale: string;
}) {
  const { t } = useTranslation('owner');
  const currencyLabel = t('analytics.currency');
  const totalPayment = data.paidRevenue + data.pendingRevenue;
  const grown = useGrowOnMount();
  const [pressedIdx, setPressedIdx] = useState<number | null>(null);

  const paidPct = totalPayment > 0 ? (data.paidRevenue / totalPayment) * 100 : 0;
  const grownPaidPct = grown ? paidPct : 0;

  const paymentCards = [
    {
      rawValue: data.paidRevenue,
      label: t('analytics.payment.paid'),
      bg: 'var(--bl-success-soft, #e8f7ee)',
      color: '#1f8b4c',
    },
    {
      rawValue: data.pendingRevenue,
      label: t('analytics.payment.pending'),
      bg: '#fff8ed',
      color: '#d46b08',
    },
  ];

  return (
    <div
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
        padding: '14px 14px 14px',
        marginBottom: 12,
      }}
    >
      <SectionHeader
        title={t('analytics.payment.title')}
        icon={<WalletOutlined />}
        accentColor="#22c55e"
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        {paymentCards.map((pc, idx) => (
          <div
            key={pc.label}
            onTouchStart={() => {
              setPressedIdx(idx);
              telegram.hapticFeedback.selection();
            }}
            onTouchEnd={() => setPressedIdx(null)}
            onTouchCancel={() => setPressedIdx(null)}
            onMouseDown={() => setPressedIdx(idx)}
            onMouseUp={() => setPressedIdx(null)}
            onMouseLeave={() => setPressedIdx(null)}
            style={{
              borderRadius: 12,
              background: pc.bg,
              padding: '12px 14px',
              transform: pressedIdx === idx ? 'scale(0.98)' : 'scale(1)',
              transition: 'transform 0.1s ease',
              WebkitTapHighlightColor: 'transparent',
              cursor: 'default',
            }}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: 'var(--bl-text-strong, #0f172a)',
                letterSpacing: -0.3,
                lineHeight: 1.15,
              }}
            >
              <AnimatedNumber
                value={pc.rawValue}
                format={(n) =>
                  formatCompactNumber(Math.round(n), currencyLabel, locale)
                }
                durationMs={1000}
              />
            </div>
            <Text
              style={{
                fontSize: 11,
                color: pc.color,
                fontWeight: 600,
                display: 'block',
                marginTop: 2,
              }}
            >
              {pc.label}
            </Text>
          </div>
        ))}
      </div>

      {/* Split bar */}
      {totalPayment > 0 && (
        <div
          style={{
            display: 'flex',
            width: '100%',
            height: 10,
            borderRadius: 6,
            overflow: 'hidden',
            background: 'var(--bl-surface-soft, #f0f2f5)',
          }}
        >
          <div
            style={{
              width: `${grownPaidPct}%`,
              background: 'linear-gradient(90deg, #1f8b4c, #2dba71)',
              transition: 'width 0.4s ease',
            }}
          />
          <div
            style={{
              flex: 1,
              background: 'linear-gradient(90deg, #faad14, #ffc53d)',
            }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function AnalyticsEmpty() {
  const { t } = useTranslation('owner');
  return (
    <div
      style={{
        textAlign: 'center',
        padding: '32px 20px',
        background: 'var(--bl-surface, #fff)',
        borderRadius: 16,
        border: '1px solid var(--bl-divider, #ebebeb)',
        marginTop: 16,
      }}
    >
      <div style={{ fontSize: 36, marginBottom: 10 }}>📊</div>
      <Text
        style={{
          fontSize: 15,
          fontWeight: 600,
          display: 'block',
          marginBottom: 4,
        }}
      >
        {t('analytics.empty.title')}
      </Text>
      <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>
        {t('analytics.empty.description')}
      </Text>
    </div>
  );
}

// ─── Error state with retry ──────────────────────────────────────────────────

function AnalyticsErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('owner');
  const [pressed, setPressed] = useState(false);

  return (
    <div
      style={{
        textAlign: 'center',
        padding: '48px 32px 32px',
        marginTop: 16,
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          background:
            'linear-gradient(135deg, rgba(250,173,20,0.14) 0%, rgba(250,173,20,0.28) 100%)',
          border: '1px solid rgba(250,173,20,0.20)',
          color: '#faad14',
          fontSize: 30,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
          boxShadow: '0 8px 24px rgba(250,173,20,0.10)',
        }}
      >
        <ReloadOutlined />
      </div>
      <Text
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: 'var(--bl-text-strong, #0f172a)',
          display: 'block',
          marginBottom: 6,
        }}
      >
        {t('analytics.errorState.title')}
      </Text>
      <Text
        style={{
          fontSize: 13,
          color: 'var(--bl-text-muted, #6b7280)',
          display: 'block',
          maxWidth: 280,
          margin: '0 auto 16px',
          lineHeight: 1.45,
        }}
      >
        {t('analytics.errorState.description')}
      </Text>

      <button
        type="button"
        onClick={onRetry}
        onTouchStart={() => setPressed(true)}
        onTouchEnd={() => setPressed(false)}
        onTouchCancel={() => setPressed(false)}
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
        onMouseLeave={() => setPressed(false)}
        style={{
          minHeight: 44,
          padding: '0 20px',
          border: '1px solid rgba(22,119,255,0.18)',
          borderRadius: 12,
          background: 'linear-gradient(135deg, #3390ec 0%, #1677ff 100%)',
          color: '#fff',
          fontSize: 14,
          fontWeight: 700,
          cursor: 'pointer',
          boxShadow:
            '0 4px 14px rgba(22,119,255,0.28), inset 0 1px 0 rgba(255,255,255,0.18)',
          transform: pressed ? 'scale(0.985)' : 'scale(1)',
          transition: 'transform 0.1s ease',
          WebkitTapHighlightColor: 'transparent',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          outline: 'none',
          letterSpacing: 0.15,
          fontFamily: 'inherit',
        }}
      >
        <ReloadOutlined />
        <span>{t('analytics.errorState.retryBtn')}</span>
      </button>
    </div>
  );
}

// ─── Premium upgrade prompt ──────────────────────────────────────────────────

function PremiumUpgradePrompt() {
  const { t } = useTranslation('owner');

  return (
    <div
      style={{
        position: 'relative',
        textAlign: 'center',
        padding: '36px 24px 28px',
        background:
          'linear-gradient(135deg, rgba(250,140,22,0.08) 0%, rgba(146,84,222,0.10) 100%)',
        border: '1px solid rgba(250,140,22,0.22)',
        borderRadius: 18,
        marginTop: 16,
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -40,
          left: -20,
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: 'rgba(250,140,22,0.18)',
          filter: 'blur(8px)',
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          bottom: -40,
          right: -20,
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: 'rgba(146,84,222,0.16)',
          filter: 'blur(8px)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'relative' }}>
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background:
              'linear-gradient(135deg, #fa8c16 0%, #d46b08 60%, #722ed1 100%)',
            color: '#fff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 30,
            margin: '0 auto 14px',
            boxShadow:
              '0 10px 28px rgba(250,140,22,0.30), inset 0 1px 0 rgba(255,255,255,0.22)',
          }}
        >
          <LockOutlined />
        </div>

        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            background: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)',
            color: '#fff',
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: 0.6,
            padding: '3px 10px',
            borderRadius: 999,
            marginBottom: 10,
            boxShadow: '0 4px 12px rgba(250,140,22,0.32)',
          }}
        >
          <CrownOutlined style={{ fontSize: 10 }} />
          {t('analytics.premium.badge')}
        </div>

        <Text
          style={{
            fontSize: 17,
            fontWeight: 800,
            color: 'var(--bl-text-strong, #0f172a)',
            display: 'block',
            marginBottom: 6,
            letterSpacing: -0.2,
          }}
        >
          {t('analytics.premium.title')}
        </Text>
        <Text
          style={{
            fontSize: 13,
            color: 'var(--bl-text-muted, #6b7280)',
            display: 'block',
            maxWidth: 300,
            margin: '0 auto 18px',
            lineHeight: 1.5,
          }}
        >
          {t('analytics.premium.description')}
        </Text>

        {/* Upgrade CTA (disabled until owner-side flow ready) */}
        <button
          type="button"
          disabled
          style={{
            minHeight: 46,
            padding: '0 22px',
            border: 'none',
            borderRadius: 12,
            background: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)',
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'not-allowed',
            opacity: 0.85,
            boxShadow:
              '0 6px 18px rgba(250,140,22,0.30), inset 0 1px 0 rgba(255,255,255,0.18)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            WebkitTapHighlightColor: 'transparent',
            outline: 'none',
            letterSpacing: 0.15,
            fontFamily: 'inherit',
          }}
        >
          <CrownOutlined />
          <span>{t('analytics.premium.upgradeBtn')}</span>
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function OwnerAnalyticsTab() {
  const { i18n } = useTranslation('owner');
  const selectedBusiness = useBusinessStore((s) => s.selectedBusiness);

  const [period, setPeriod] = useState<PeriodKey>('month');
  const [data, setData] = useState<OwnerAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AnalyticsError | null>(null);

  const locale = LOCALE_MAP[i18n.language] ?? 'uz-UZ';

  const loadData = useCallback(
    async (selectedPeriod: PeriodKey) => {
      if (!selectedBusiness) return;
      setLoading(true);
      setError(null);
      try {
        const res = await businessApi.getAnalytics(
          selectedBusiness.id,
          selectedPeriod,
        );
        setData(res);
      } catch (err) {
        setError(classifyError(err));
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [selectedBusiness],
  );

  useEffect(() => {
    void loadData(period);
  }, [loadData, period]);

  // Backend labels are localized via Accept-Language — refetch when language changes
  useEffect(() => {
    const handler = () => {
      void loadData(period);
    };
    i18n.on('languageChanged', handler);
    return () => {
      i18n.off('languageChanged', handler);
    };
  }, [i18n, loadData, period]);

  const hasAnyData = useMemo(() => {
    if (!data) return false;
    return data.totalBookings > 0 || data.totalRevenue > 0;
  }, [data]);

  if (!selectedBusiness) return null;

  // Stagger delays for each top-level block (70ms per block)
  const blockDelay = (i: number) => ({ animationDelay: `${i * 70}ms` });

  return (
    <div>
      {/* Period switcher — shown when not in premium-gated state */}
      {error !== 'premium' && (
        <div
          className="bl-animate-in"
          style={{ marginBottom: 12, ...blockDelay(0) }}
        >
          <PeriodSwitcher value={period} onChange={setPeriod} />
        </div>
      )}

      {loading ? (
        <OwnerAnalyticsSkeleton />
      ) : error === 'premium' ? (
        <PremiumUpgradePrompt />
      ) : error === 'generic' ? (
        <AnalyticsErrorState onRetry={() => void loadData(period)} />
      ) : !data || !hasAnyData ? (
        <AnalyticsEmpty />
      ) : (
        <div key="ready">
          {/* hero=1 */}
          <div className="bl-animate-in" style={blockDelay(1)}>
            <HeroRevenueCard data={data} locale={locale} />
          </div>
          {/* kpi=2 */}
          <div className="bl-animate-in" style={blockDelay(2)}>
            <KpiGrid data={data} locale={locale} />
          </div>
          {/* trend=3 */}
          <div className="bl-animate-in" style={blockDelay(3)}>
            <RevenueTrendChart data={data} />
          </div>
          {/* status=4 */}
          <div className="bl-animate-in" style={blockDelay(4)}>
            <StatusBreakdown data={data} />
          </div>

          {/* Provider breakdown */}
          {/* topProviders=5 */}
          <div className="bl-animate-in" style={blockDelay(5)}>
            <TopProvidersList data={data} locale={locale} />
          </div>
          {/* revenueShare=6 */}
          <div className="bl-animate-in" style={blockDelay(6)}>
            <RevenueShareBar data={data} />
          </div>
          {/* utilization=7 */}
          <div className="bl-animate-in" style={blockDelay(7)}>
            <UtilizationList data={data} />
          </div>

          {/* Time patterns */}
          {/* weekdayLoad=8 */}
          <div className="bl-animate-in" style={blockDelay(8)}>
            <WeekdayLoadChart data={data} />
          </div>

          {/* Service and customer detail */}
          {/* topServices=9 */}
          <div className="bl-animate-in" style={blockDelay(9)}>
            <TopServicesList data={data} locale={locale} />
          </div>
          {/* topCustomers=10 */}
          <div className="bl-animate-in" style={blockDelay(10)}>
            <TopCustomersList data={data} locale={locale} />
          </div>
          {/* peakHours=11 */}
          <div className="bl-animate-in" style={blockDelay(11)}>
            <PeakHoursChart data={data} />
          </div>

          {/* Operational metrics */}
          {/* customersBreakdown=12 */}
          <div className="bl-animate-in" style={blockDelay(12)}>
            <CustomersBreakdown data={data} />
          </div>
          {/* noShow=13 */}
          <div className="bl-animate-in" style={blockDelay(13)}>
            <NoShowCard data={data} />
          </div>
          {/* payment=14 */}
          <div className="bl-animate-in" style={blockDelay(14)}>
            <PaymentCard data={data} locale={locale} />
          </div>
        </div>
      )}
    </div>
  );
}
